import { sortBy, uniqueId } from 'lodash';

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getDaysInMonthDisplay } from '../services/date.service';
import styled, { css } from 'styled-components';
import IconButtonComponent from './IconButton.component';
import ChevronLeftIcon from './Icons/ChevronLeft.icon';
import ChevronRightIcon from './Icons/ChevronRight.icon';
import { WEEKDAYS } from '../constants/date.contant';

const isBetween = require('dayjs/plugin/isBetween');
dayjs.extend(isBetween);

export type DateRangeType = {
  start: number;
  end: number;
};

export interface CalendarProps {
  date?: number;
  onChange: Function;
  dateRange?: DateRangeType;
  withoutDaysBefore?: boolean;
  withoutDaysAfter?: boolean;
}

const StyledCalendarDaysDayNoCurrentMonth = css`
  pointer-events: none;
  color: #cacaca;
`;

const StyledCalendarDaysBeforeCurrentDay = css`
  color: #cacaca;
  margin: 1px;
  pointer-events: none;
`;

const StyledCalendarDaysDayActive = css`
  background: ${({ theme }) => theme.colors.BLUE.B400};
  color: ${({ theme }) => theme.colors.CARD};
  font-weight: 500;
`;

const StyledCalendarDaysDayToday = css`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.BLUE.B400};
`;

const StyledCalendarDaysDayHover = css`
  :hover {
    border-color: ${({ theme }) => theme.colors.BORDER};
  }
`;

const StyledCalendarDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;

const StyledCalendarDaysDay = styled.div<{
  isToday: boolean;
  isActive: boolean;
  isCurrentMonth: boolean;
  inBeth: boolean;
  isFirstActive: boolean;
  isSecondActive: boolean;
  isBeforeCurrentDay?: boolean;
  isAfterCurrentDay?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  position: relative;
  border: 1px solid transparent;
  transition: all 0.3s;
  cursor: pointer;

  :before {
    content: '';
    width: 50%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${props =>
      props.isCurrentMonth && props.theme.colors.BLUE.B400};
    opacity: ${props => (props.inBeth ? 0.3 : 0)};
  }

  :after {
    content: '';
    width: 50%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    background-color: ${props =>
      props.isCurrentMonth && props.theme.colors.BLUE.B400};
    opacity: ${props => (props.inBeth ? 0.3 : 0)};
  }

  ${props => props.isBeforeCurrentDay && StyledCalendarDaysBeforeCurrentDay};
  ${props => props.isAfterCurrentDay && StyledCalendarDaysBeforeCurrentDay};
  ${props => !props.isCurrentMonth && StyledCalendarDaysDayNoCurrentMonth};
  ${props => props.isActive && StyledCalendarDaysDayActive};
  ${props => !props.isActive && StyledCalendarDaysDayHover};
  ${props => props.isToday && StyledCalendarDaysDayToday};
  ${props =>
    props.isToday && props.isActive && `color: ${props.theme.colors.CARD}`};
`;
const StyledCalendarWeekdaysDay = styled.div`
  color: ${({ theme }) => theme.colors.LABEL};
  padding: 8px;
  flex: 1 1 0;
  text-align: center;
  font-size: 12px;
  font-weight: 500;

  &:nth-last-child(-n + 2) {
    color: ${({ theme }) => theme.colors.RED.R400};
  }
`;
const StyledCalendarWeekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`;
const StyledCalendarHeaderTitle = styled.div``;
const StyledCalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
`;
const StyledCalendar = styled.div``;

const CalendarComponent = ({
  date,
  onChange,
  dateRange,
  withoutDaysAfter,
  withoutDaysBefore,
}: CalendarProps) => {
  const { t } = useTranslation();
  const [hideDate, setHideDate] = useState(date || dayjs().unix() * 1000);
  const [selectedList, setSelectedList] = useState([
    dateRange?.start,
    dateRange?.end
  ]);

  const onPushPrevMonth = () => {
    const _date =
      dayjs(hideDate)
        .subtract(1, 'month')
        .startOf('month')
        .set('hour', 0)
        .set('minute', 0)
        .set('second', 0)
        .unix() * 1000;

    setHideDate(_date);
  };

  const onPushNextMonth = () => {
    const _date =
      dayjs(hideDate)
        .add(1, 'month')
        .startOf('month')
        .set('hour', 0)
        .set('minute', 0)
        .set('second', 0)
        .unix() * 1000;

    setHideDate(_date);
  };

  const isActive = useCallback(
    (_date: Date) => {
      return dayjs(_date).isSame(dayjs(date), 'day');
    },
    [date],
  );

  const isToday = (date: Date) => {
    return dayjs(date).isSame(dayjs(), 'day');
  };

  const handleDayClick = (_date: number) => {
    _date =
      dayjs(_date).set('hour', 0).set('minute', 0).set('second', 0).unix() *
      1000;
    let newList = [...selectedList];

    if (dateRange) {
      const dayIndex = newList.indexOf(_date);

      if (dayIndex > -1) {
        newList = [
          ...newList.slice(0, dayIndex),
          ...newList.slice(dayIndex + 1),
        ];
      } else if (newList.length > 1) {
        newList = [_date];
      } else {
        newList.push(_date);
      }
      newList = sortBy(newList);
    } else {
      newList = [_date];
    }

    setSelectedList(newList);
    setHideDate(newList[0] || hideDate);
    onChange({
      start: newList[0],
      end: newList[1] || newList[0],
    });
  };

  const days = useMemo(() => {
    return getDaysInMonthDisplay(hideDate).map(
      dayEntry => {
        const inBeth = dayjs(dayEntry.date)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          // @ts-ignore
          .isBetween(
            dayjs(selectedList[0]),
            dayjs(selectedList[1] || selectedList[0]),
          );

        const equalFirstDate = dayjs(dayEntry.date)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          .isSame(dayjs(selectedList[0]));

        const equalSecondDate = dayjs(dayEntry.date)
          .set('hour', 0)
          .set('minute', 0)
          .set('second', 0)
          .isSame(dayjs(selectedList[1] || selectedList[0]));

        const isFirstDate =
          equalFirstDate && !equalSecondDate && selectedList.length > 1;

        const isSecondDate =
          equalSecondDate && !equalFirstDate && selectedList.length > 1;

        const currentDate = dayjs().unix() * 1000;

        const isBeforeCurrentDay = dayjs(dayEntry.date).isBefore(
          dayjs(currentDate),
          'day',
        );

        const isAfterCurrentDay = dayjs(dayEntry.date).isAfter(
          dayjs(currentDate),
          'day',
        );

        return (
          <StyledCalendarDaysDay
            isBeforeCurrentDay={
              withoutDaysBefore ? isBeforeCurrentDay : undefined
            }
            isAfterCurrentDay={withoutDaysAfter ? isAfterCurrentDay : undefined}
            key={uniqueId('calendar-day-')}
            isToday={isToday(dayEntry.date)}
            isActive={
              dateRange ? equalFirstDate || equalSecondDate : isActive(dayEntry.date)
            }
            inBeth={inBeth}
            isFirstActive={isFirstDate}
            isSecondActive={isSecondDate}
            isCurrentMonth={dayEntry.currentMonth}
            onClick={() => handleDayClick(dayEntry.date.getTime())}
          >
            {dayjs(dayEntry.date).format('D')}
          </StyledCalendarDaysDay>
        );
      },
    );
  }, [hideDate, selectedList]);

  return (
    <StyledCalendar>
      <StyledCalendarHeader>
        <IconButtonComponent
          mode="transparent"
          onClick={() => onPushPrevMonth()}
        >
          <ChevronLeftIcon />
        </IconButtonComponent>
        <StyledCalendarHeaderTitle>
          {t(`months.${dayjs(hideDate).format('MMMM')}`)}{' '}
          {dayjs(hideDate).format('YYYY')}
        </StyledCalendarHeaderTitle>
        <IconButtonComponent
          mode="transparent"
          onClick={() => onPushNextMonth()}
        >
          <ChevronRightIcon />
        </IconButtonComponent>
      </StyledCalendarHeader>
      <StyledCalendarWeekdays>
        {WEEKDAYS.map((day: string) => (
          <StyledCalendarWeekdaysDay key={uniqueId('calendar-weekday-')}>
            {t(`weekdays.${day}`)}
          </StyledCalendarWeekdaysDay>
        ))}
      </StyledCalendarWeekdays>
      <StyledCalendarDays>{days}</StyledCalendarDays>
    </StyledCalendar>
  );
};

export default CalendarComponent;
